import { useCollectionServices } from '../../contexts/CollectionSourceContext';
import React, { useEffect, useState } from 'react';
import { Alert, Button, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { FitbitReadParticipant, FitbitSummary } from '../../services/fitbitCollectionDataService';

type Row = { participant: FitbitReadParticipant; summary?: FitbitSummary; failed?: boolean };
export default function FitbitCollectionStudyOverview({ studyId, participants, startDate, endDate, onSelect }: {
  studyId: string; participants: FitbitReadParticipant[]; startDate: string; endDate: string; onSelect: (id: string) => void;
}) {
  const { readFitbitData } = useCollectionServices();
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => { setPage(0); }, [participants, studyId]);
  useEffect(() => {
    let current = true;
    const selected = participants.slice(page * 20, (page + 1) * 20);
    setRows([]); setLoading(true);
    const run = async () => {
      const result: Row[] = [];
      // Three concurrent requests at most; each read independently enforces study access.
      for (let i = 0; i < selected.length && current; i += 3) {
        result.push(...await Promise.all(selected.slice(i, i + 3).map(async participant => {
          if (participant.enrollmentStatus !== 'completed') return { participant };
          try { return { participant, summary: await readFitbitData<FitbitSummary>(studyId, participant.participantId, 'summary', { startDate, endDate }) }; }
          catch { return { participant, failed: true }; }
        })));
      }
      if (current) { setRows(result); setLoading(false); }
    };
    void run();
    return () => { current = false; };
  }, [studyId, participants, startDate, endDate, page, readFitbitData]);
  return <Paper sx={{ p: 2, mb: 3, overflow: 'auto' }}>
    <Typography variant="h6">Study overview</Typography>
    <Typography color="text.secondary">The selected date range applies to every participant. Adherence averages include only collected, complete days with supported thresholds.</Typography>
    {loading ? <CircularProgress aria-label="Loading study overview" /> : <>
      {rows.some(row => row.failed) && <Alert severity="warning">Some participant data could not be loaded. Those results are unavailable.</Alert>}
      <Table size="small"><TableHead><TableRow>
        {['Participant', 'Collection', 'Metrics with data', 'Scored days', 'Average adherence', 'Days below 70%'].map(label => <TableCell key={label}>{label}</TableCell>)}
      </TableRow></TableHead><TableBody>{rows.map(({ participant, summary, failed }) => {
        const scores = summary?.adherence.days.filter(day => day.adherence !== null) || [];
        const average = scores.length ? scores.reduce((sum, day) => sum + day.adherence!, 0) / scores.length : null;
        return <TableRow key={participant.participantId}>
          <TableCell><Button onClick={() => onSelect(participant.participantId)}>{participant.name}</Button></TableCell>
          <TableCell>{failed ? 'Unavailable' : participant.enrollmentStatus !== 'completed' ? 'Pending enrollment' : summary?.campaigns[0]?.state || 'Waiting to start'}</TableCell>
          <TableCell>{summary ? `${summary.metrics.filter(metric => Number(metric.presentRecords) > 0).length} / ${summary.catalog.length}` : 'Unknown'}</TableCell>
          <TableCell>{summary ? scores.length : 'Unknown'}</TableCell>
          <TableCell>{average === null ? 'Unknown' : `${average.toFixed(1)}%`}</TableCell>
          <TableCell>{scores.length ? scores.filter(day => day.adherence! < 70).length : 'Unknown'}</TableCell>
        </TableRow>;
      })}</TableBody></Table>
    </>}
    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
      <Button disabled={page === 0} onClick={() => setPage(n => n - 1)}>Previous participants</Button>
      <Typography>Page {page + 1} of {Math.max(1, Math.ceil(participants.length / 20))}</Typography>
      <Button disabled={(page + 1) * 20 >= participants.length} onClick={() => setPage(n => n + 1)}>Next participants</Button>
    </Stack>
  </Paper>;
}
